import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

import {
  login as loginApi,
  register as registerApi,
  refreshToken as refreshTokenApi,
  logout as logoutApi,
  type LoginParams,
  type RegisterParams,
  type LoginResult,
} from '@/api/auth';

/**
 * 认证 Store
 *
 * - 管理 token / userInfo
 * - 提供 login / register / logout / refreshActions
 * - Token 持久化到 localStorage
 */
export const useAuthStore = defineStore('auth', () => {
  // ---- state ----
  const accessToken = ref<string>(localStorage.getItem('access_token') || '');
  const refreshToken = ref<string>(localStorage.getItem('refresh_token') || '');
  const expiresAt = ref<number>(Number(localStorage.getItem('token_expires_at')) || 0);
  const userInfo = ref<LoginResult['user'] | null>(
    JSON.parse(localStorage.getItem('user_info') || 'null'),
  );

  // ---- getters ----
  const isLoggedIn = computed(() => !!accessToken.value);
  const accountId = computed(() => userInfo.value?.accountId || 0);
  const nickname = computed(() => userInfo.value?.nickname || '');
  const avatar = computed(() => userInfo.value?.avatar || '');

  // ---- actions ----

  /**
   * 持久化 Token
   */
  function persistTokens(access: string, refresh: string, expiresIn: number): void {
    accessToken.value = access;
    refreshToken.value = refresh;
    expiresAt.value = Date.now() + expiresIn * 1000;
    localStorage.setItem('access_token', access);
    localStorage.setItem('refresh_token', refresh);
    localStorage.setItem('token_expires_at', String(expiresAt.value));
  }

  /**
   * 持久化用户信息
   */
  function persistUser(user: LoginResult['user']): void {
    userInfo.value = user;
    localStorage.setItem('user_info', JSON.stringify(user));
  }

  /**
   * 登录
   */
  async function login(params: LoginParams): Promise<void> {
    const result = await loginApi(params);
    persistTokens(result.accessToken, result.refreshToken, result.expiresIn);
    persistUser(result.user);
  }

  /**
   * 注册
   */
  async function register(params: RegisterParams): Promise<LoginResult> {
    const result = await registerApi(params);
    persistTokens(result.accessToken, result.refreshToken, result.expiresIn);
    persistUser(result.user);
    return result;
  }

  /**
   * 刷新 Token
   */
  async function refresh(): Promise<void> {
    if (!refreshToken.value) {
      throw new Error('无 Refresh Token');
    }
    const result = await refreshTokenApi(refreshToken.value);
    persistTokens(result.accessToken, result.refreshToken, result.expiresIn);
  }

  /**
   * 退出登录
   */
  async function logout(): Promise<void> {
    try {
      await logoutApi();
    } catch {
      // 忽略退出登录的接口错误
    }
    clearAuth();
  }

  /**
   * 清除本地认证信息
   */
  function clearAuth(): void {
    accessToken.value = '';
    refreshToken.value = '';
    expiresAt.value = 0;
    userInfo.value = null;
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('token_expires_at');
    localStorage.removeItem('user_info');
  }

  return {
    // state
    accessToken,
    refreshToken,
    expiresAt,
    userInfo,
    // getters
    isLoggedIn,
    accountId,
    nickname,
    avatar,
    // actions
    login,
    register,
    refresh,
    logout,
    clearAuth,
    persistTokens,
    persistUser,
  };
});
